import { ClientEvents } from 'common/events/ClientEvents';
import { QueryParams } from 'common/urls/QueryParams';
import { first, isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstFoodItemPreviewFragment, DishFirstSegmentFilter } from 'src/gqlReactTypings.generated.d';
import { SeeAllCard } from 'src/pages/consumer/dish-first/explore/components/SeeAllCard';
import { useDishModalActions } from 'src/pages/consumer/dish-first/explore/store';
import {
  SegmentHeader,
  SegmentHeaderContainer,
  SegmentHeaderRow,
  SegmentHeaderSubtitle,
  SegmentHeaderTitle,
} from 'src/pages/consumer/dish-first/explore/styles';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { Carousel } from 'src/shared/design-system/DishFirst/Carousel';
import { useFilterQueryParams } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { useTracker } from 'src/shared/hooks/useTracker';
import { ExploreDishCard } from './ExploreDishCard';
import { MaxWidthContainer, RowContainer, SeeAllButton } from './styles';
import { DishFirstCardRowProps } from './types';
import { getQueryParamsFromSeeAllFilter } from './utils';

export const trackSeeAllClick = (
  tracker,
  segmentMetrics: ISegmentMetrics,
  type: 'dish' | 'shef',
  filtersQueryParams
) => {
  tracker.track(ClientEvents.SEGMENT_SEE_ALL_LINK_CLICK, {
    segmentId: segmentMetrics.esid,
    segmentRenderedItems: segmentMetrics.renderedItems,
    segmentTotalItems: segmentMetrics.totalItems,
    position: segmentMetrics.pos,
    type,
    filters: {
      spiceFilters: filtersQueryParams[QueryParams.SPICE_LEVEL_FILTER],
      cuisineFilters: filtersQueryParams[QueryParams.CUISINE_FILTER],
      dietaryFilters: filtersQueryParams[QueryParams.DIETARY_FILTER],
      dishTypeFilters: filtersQueryParams[QueryParams.DISH_TYPE_FILTER],
      priceFilters: filtersQueryParams[QueryParams.PRICE_LEVEL_FILTER],
      dateFilters: filtersQueryParams[QueryParams.DATE],
      proteinFilters: filtersQueryParams[QueryParams.PROTEIN_FILTER],
    },
  });
};

const isFoodItem = (item): item is DishFirstFoodItemPreviewFragment => item.__typename === 'FoodItem';
const isSegmentFilter = (id): id is DishFirstSegmentFilter => Object.values(DishFirstSegmentFilter).includes(id);

export const DishFirstCardRow: React.FC<DishFirstCardRowProps> = ({
  className,
  lineItemAvailabilities,
  segment,
  segmentMetrics,
  scrollPosition,
  updateQueryParams,
  location,
}): JSX.Element => {
  const [deliveryDates] = usePersistentDeliveryDates();
  const deliveryDate = first(deliveryDates);
  const { openDishModal } = useDishModalActions();

  const items: DishFirstFoodItemPreviewFragment[] = useMemo(
    () => segment.content.items.filter(isFoodItem),
    [segment.content.items]
  );
  const tracker = useTracker();
  const { filtersQueryParams } = useFilterQueryParams();

  const handleSeeAllButtonClick = useCallback(() => {
    if (segmentMetrics) {
      trackSeeAllClick(tracker, segmentMetrics, 'dish', filtersQueryParams);
    }
    if (segment.preview?.seeAllFilter) {
      updateQueryParams(getQueryParamsFromSeeAllFilter(segment.preview.seeAllFilter));
    } else if (isSegmentFilter(segment.id)) {
      updateQueryParams({ [QueryParams.SEGMENT_FILTER]: [segment.id] });
    }
  }, [tracker, segment.id, updateQueryParams]);

  return (
    <MaxWidthContainer className={className}>
      <SegmentHeaderContainer>
        <SegmentHeaderRow>
          <SegmentHeader>
            <SegmentHeaderTitle>{segment.displayName}</SegmentHeaderTitle>
          </SegmentHeader>
          {segment.id !== 'ALL' && <SeeAllButton onClick={handleSeeAllButtonClick}>See all</SeeAllButton>}
        </SegmentHeaderRow>
        {segment.subtitle && (
          <SegmentHeaderRow>
            <SegmentHeaderSubtitle>{segment.subtitle}</SegmentHeaderSubtitle>
          </SegmentHeaderRow>
        )}
      </SegmentHeaderContainer>
      <RowContainer>
        <Carousel content={items} scrollButtonsElementName={segment.id} showMobileDivider showScrollButtons>
          <>
            {items?.map((item, idx) => {
              const itemAvailability = first(item.availability);
              const nextDeliveryDate = itemAvailability?.availabilityDate
                ? DateTime.fromISO(itemAvailability.availabilityDate)
                : null;

              return (
                <ExploreDishCard
                  key={item.foodItemId}
                  deliveryDate={deliveryDate}
                  idx={idx}
                  foodItemPreview={item}
                  lineItemAvailabilities={lineItemAvailabilities}
                  onClick={openDishModal}
                  nextDeliveryDate={isUndefined(deliveryDate) ? nextDeliveryDate : undefined}
                  segment={segment}
                  segmentMetrics={segmentMetrics}
                  location={location}
                  isResponsive={false}
                  scrollPosition={scrollPosition}
                />
              );
            })}
            <SeeAllCard onClick={handleSeeAllButtonClick} />
          </>
        </Carousel>
      </RowContainer>
    </MaxWidthContainer>
  );
};
